<template>
  <v-container fluid class="storage-container pa-0">
    <!-- Modern Header -->
    <div
      v-if="showHeader"
      class="header-section mb-4"
      style="margin: 0 12px; position: relative;"
    >
      <v-btn icon small class="header-close-btn" @click="dismissHeader">
        <v-icon color="white">mdi-close</v-icon>
      </v-btn>
      <div class="d-flex align-center mb-4">
        <div class="header-icon-wrapper mr-4">
          <v-icon size="32" color="#FFFFFF">mdi-database</v-icon>
        </div>
        <div class="flex-grow-1">
          <h1 class="page-title mb-1">Storage Management</h1>
          <p class="page-subtitle mb-0">
            Monitor and manage your storage usage
          </p>
        </div>
        <v-spacer></v-spacer>
        <div class="d-none d-sm-flex">
          <v-chip
            class="mr-2 white--text"
            color="white"
            small
            style="background: rgba(255,255,255,0.25) !important; border: 1px solid rgba(255,255,255,0.5);"
          >
            <v-icon left x-small color="white">mdi-cloud-upload</v-icon>
            Cloud Storage
          </v-chip>
          <v-chip
            class="white--text"
            color="white"
            small
            style="background: rgba(255,255,255,0.25) !important; border: 1px solid rgba(255,255,255,0.5);"
          >
            <v-icon left x-small color="white">mdi-chart-line</v-icon>
            Analytics
          </v-chip>
        </div>
      </div>

      <!-- Feature Pills -->
      <v-row class="feature-pills ma-0">
        <v-col cols="12" sm="6" md="4" lg="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2"
              >mdi-file-document</v-icon
            >
            <span>File Management</span>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2">mdi-chart-pie</v-icon>
            <span>Usage Stats</span>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2"
              >mdi-cloud-upload</v-icon
            >
            <span>Cloud Storage</span>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2"
              >mdi-folder-open</v-icon
            >
            <span>Organization</span>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2">mdi-resize</v-icon>
            <span>Quota Management</span>
          </div>
        </v-col>
      </v-row>
    </div>

    <v-card elevation="0" class="transparent">
      <v-layout>
        <v-flex xs12 sm12 md12 class="px-3">
          <v-card class="modern-card customBorderRadius" elevation="0">
            <div class="card-header-green"></div>
            <v-card-title class="pt-6 pb-4">
              <v-icon left color="primary">mdi-file-multiple</v-icon>
              <span class="text-h6 font-weight-medium mr-4">Files</span>
              <v-spacer></v-spacer>
              <div class="storage-usage-info">
                <p class="mb-1 text-caption">Total Usage</p>
                <h3 v-if="$store.getters.companyDetails.usage < 1024">
                  {{
                    $store.getters.companyDetails.usage == "null" ||
                    $store.getters.companyDetails.usage == 0
                      ? mathfloorCustom(0)
                      : mathfloorCustom(
                          $store.getters.companyDetails.usage || 0,
                        )
                  }}
                  /
                  {{
                    mathfloorCustom(
                      $store.getters.companyDetails.plan.storageLimit,
                    )
                  }}
                </h3>
                <h3 v-else>
                  {{ $store.getters.companyDetails.usage / 1024 }}GB /
                  {{
                    Math.round(
                      $store.getters.companyDetails.plan.storageLimit / 1024,
                    )
                  }}GB
                </h3>
                <v-progress-linear
                  color="primary"
                  height="10"
                  rounded
                  class="mt-2"
                  :value="
                    ($store.getters.companyDetails.usage /
                      $store.getters.companyDetails.plan.storageLimit) *
                      100
                  "
                ></v-progress-linear>
              </div>
            </v-card-title>
            <v-card-text>
              <v-data-table
                :headers="fileTableHeaders"
                :items="files"
                :loading="loading"
                :items-per-page="records"
                item-key="id"
                hide-default-footer
              ></v-data-table>
              <v-divider style="margin-bottom: 10px"></v-divider>
            </v-card-text>
            <v-card-text>
              <div class="v-data-footer">
                <div class="v-data-footer__select">
                  Rows per page:
                  <div
                    class="v-input v-input--hide-details v-input--is-label-active v-input--is-dirty theme--light v-text-field v-text-field--is-booted v-select"
                  >
                    <v-select
                      v-model="records"
                      :items="recordsPerPage"
                      style="margin: 5px"
                      @change="pagination"
                    ></v-select>
                  </div>
                </div>
                <div class="v-data-footer__select">
                  <div class="v-data-footer__icons-before">
                    <v-btn
                      :disabled="page == 1 ? true : false"
                      color="primary"
                      fab
                      small
                      text
                      @click="prevSet"
                    >
                      <v-icon :size="24">arrow_left</v-icon>
                    </v-btn>
                  </div>
                  <div class="v-data-footer__pagination">
                    {{ `${page}/${pageCount} of ${totalRecords}` }}
                  </div>
                  <div class="v-data-footer__icons-after">
                    <v-btn
                      :disabled="page >= pageCount ? true : false"
                      color="primary"
                      fab
                      small
                      text
                      @click="nextSet"
                    >
                      <v-icon :size="24">arrow_right</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>

      <v-layout>
        <v-flex xs12 sm12 md12 class="px-3">
          <v-card class="modern-card customBorderRadius" elevation="0">
            <div class="card-header-green"></div>
            <v-card-title class="pt-6 pb-4">
              <v-icon left color="primary">mdi-database-plus</v-icon>
              <span class="text-h6 font-weight-medium">Upgrade Storage</span>
            </v-card-title>
            <v-card-text class="pa-6 text-center">
              <v-icon size="64" color="grey lighten-2" class="mb-4"
                >mdi-cloud-upload</v-icon
              >
              <h6 class="text-h6 font-weight-light mb-4">
                Need more storage space?
              </h6>
              <p class="text-body-2 text--secondary mb-6">
                Please contact us at support@godial.cc
              </p>
              <v-btn
                color="primary"
                depressed
                large
                style="border-radius: 8px;"
                href="mailto:support@godial.cc"
              >
                <v-icon left>mdi-email</v-icon>
                Contact Us
              </v-btn>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </v-card>
  </v-container>
</template>

<script>
import moment from "moment-timezone";
import _ from "lodash";
export default {
  data() {
    return {
      showHeader: false,
      recordCallsWithTrueCaller: false,
      enableRecording: false,
      addStorage: 0,
      loading: false,
      recordsPerPage: [1, 5, 15, 30, 60],

      page: 1,
      pageCount: 0,
      totalRecords: 0,
      records: 5,

      files: [],
      fileTableHeaders: [
        {
          text: "File Name",
          align: "start",
          sortable: false,
          value: "name",
        },
        { text: "Size", value: "size", sortable: false },
        { text: "Time", value: "createdOn", sortable: false },
        { text: "Category", value: "typeOfFile", sortable: true },
      ],
    };
  },
  async created() {
    this.showHeader = localStorage.getItem("storageHeaderDismissed") !== "true";
    await this.fetchFiles();
    this.recordCallsWithTrueCaller = this.$store.state.companyDetails.recordCallsWithTrueCaller;
    await this.pagination();

    console.log(
      "$store.getters.companyDetails.usage",
      this.$store.getters.companyDetails.usage,
    );
  },

  // async mounted() {
  //   this.pagination();
  // },
  methods: {
    dismissHeader() {
      this.showHeader = false;
      localStorage.setItem("storageHeaderDismissed", "true");
    },
    mathfloorCustom(value) {
      // console.log("value", value);

      if (value == 0) {
        return "0MB";
      }

      var filterValueInBytes = value * 1024 * 1024;

      var sizes = ["Bytes", "KB", "MB", "GB", "TB"];
      var i = parseInt(
        Math.floor(Math.log(filterValueInBytes) / Math.log(1024)),
      );

      var finalValueData =
        Math.round(filterValueInBytes / Math.pow(1024, i), 2) + "" + sizes[i];

      return finalValueData;
    },
    updateRecordCalls() {
      var self = this;

      self.$http
        .patch(
          `${this.$store.getters.ENDPOINT}/companies/${self.$store.getters.user.companyId}`,
          { recordCallsWithTrueCaller: self.recordCallsWithTrueCaller },
        )
        .then((response) => {
          self.$store.dispatch("getCompanyDetails", {
            companyId: self.$store.getters.user.companyId,
          });
          console.log("response", response.body);
        })
        .catch((err) => {
          self.$swal({
            type: "error",
            text: "Something went wrong. Couldn't Update",
          });
        });
    },
    nextSet() {
      this.page++;
      this.pagination();
    },
    prevSet() {
      this.page--;
      this.pagination();
    },
    async pagination() {
      // console.log("Pagination startedddd/................")
      try {
        // console.log("total records: ", this.totalRecords, this.records);
        this.pageCount = Math.ceil(this.totalRecords / this.records);

        // console.log("Page count started: ", this.pageCount);
        if (this.page > this.pageCount) {
          this.page = 1; //set default
        }
        this.files = await this.fetchFiles(
          this.records,
          this.records * this.page - this.records,
        );

        // Save default pagination of seperate pages
        this.paginate[
          this.$router.currentRoute.name
            .toString()
            .trim()
            .replace(/ /g, "")
            .toLowerCase()
        ] = {
          page: this.page,
          pageCount: this.pageCount,
          records: this.records,
          totalRecords: this.totalRecords,
        };
        this.$store.commit("setPagination", this.paginate);

        return;
      } catch (e) {
        this.loader = false;
      }
    },

    async fetchFiles(limit, skip) {
      if (typeof limit == "undefined" && typeof skip == "undefined") {
        try {
          var where = {
            companyId: this.$store.getters.user.companyId,
          };
          var response = await this.$http.get(
            this.$store.getters.ENDPOINT +
              "/attachments/count?where=" +
              encodeURIComponent(JSON.stringify(where)),
          );

          // console.log("attchment response: ", response.body);

          this.totalRecords = response.body.count;
        } catch (e) {
          console.log(e);
        }
      }
      this.loading = true;

      this.files = [];
      if (limit == undefined || limit == undefined) {
        limit = 5;
        skip = 0;
      }
      var filter = {
        skip: skip,
        limit: limit,
        order: "createdOn DESC",
        where: {
          companyId: this.$store.getters.user.companyId,
          // typeOfFile: "recording",
        },
      };

      var res = await this.$http.get(
        this.$store.getters.ENDPOINT +
          "/attachments?filter=" +
          encodeURIComponent(JSON.stringify(filter)),
      );

      // console.log("attchment", res.body);

      this.files = _.map(res.body, (c) => {
        c.size = this.mathfloorCustom(c.size);
        c.createdOn = moment(c.createdOn).format("lll");
        return c;
      });

      console.log(" this.files", this.files);

      this.loading = false;
      return this.files;
    },

    upgradeStorage() {},
  },
};
</script>

<style scoped>
@media (min-width: 1200px) {
  .recordPadding {
    padding-left: 30%;
    padding-right: 30%;
  }
}

/* Landscape tablets and medium desktops */
@media (min-width: 992px) and (max-width: 1199px) {
  .recordPadding {
    padding-left: 30%;
    padding-right: 30%;
  }
}

/* Portrait tablets and small desktops */
@media (min-width: 768px) and (max-width: 991px) {
  .recordPadding {
    padding-left: 20%;
    padding-right: 20%;
  }
}

.storage-container {
  background-color: var(--v-background-base, #f5f5f5);
  min-height: 100vh;
}

.theme--dark .storage-container {
  background-color: #121212 !important;
}

.header-section {
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  border-radius: 12px;
  padding: 32px;
  color: white;
  box-shadow: 0 4px 20px rgba(55, 207, 55, 0.3);
}

.header-icon-wrapper {
  width: 64px;
  height: 64px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.page-title {
  font-size: 32px;
  font-weight: 600;
  color: white;
  margin: 0;
  line-height: 1.2;
}

.page-subtitle {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

.feature-pills {
  margin-top: 24px;
}

.feature-pill {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 12px 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.feature-pill:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-2px);
}

.modern-card {
  background: var(--v-background-base, white);
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.theme--dark .modern-card {
  background: #1e1e1e !important;
  border-color: #333333 !important;
}

.customBorderRadius {
  border-radius: 12px !important;
  overflow: hidden !important;
}

.card-header-green {
  height: 4px;
  background: linear-gradient(90deg, #37cf37 0%, #2ba82b 100%);
  border-radius: 12px 12px 0 0;
}

.storage-usage-info {
  text-align: right;
  min-width: 200px;
}

.modern-table {
  border-radius: 8px;
}

.pagination-wrapper {
  display: flex;
  align-items: center;
  gap: 16px;
}

.pagination-label {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #616161;
}

.pagination-select {
  max-width: 80px;
  display: inline-block;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-info {
  font-size: 14px;
  color: #616161;
  min-width: 120px;
  text-align: center;
}

/* Landscape phones and portrait tablets */
@media (max-width: 767px) {
  .recordPadding {
    padding-left: 1%;
    padding-right: 1%;
  }

  .header-icon-wrapper {
    width: 56px;
    height: 56px;
  }

  .page-title {
    font-size: 28px;
  }

  .storage-usage-info {
    text-align: left;
    min-width: auto;
  }
}

/* Portrait phones and smaller */
@media (max-width: 480px) {
  .recordPadding {
    padding-left: 1%;
    padding-right: 1%;
  }

  .pagination-wrapper {
    flex-direction: column;
    gap: 12px;
  }
}
</style>
